/**
 * ===================================================================
 * REACT HOOK - CROP PLANS
 * ===================================================================
 * Hook pro spravu planu pestovani s React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cropPlanService } from '../services/cropPlanService';
import type {
  CropPlanStatus,
  CreateCropPlanDTO,
  UpdateCropPlanDTO,
  CreateCropPlanItemDTO,
  CreatePlacementDTO,
} from '../types/crop-plans';

// Query keys
const QUERY_KEYS = {
  plans: ['crop-plans'] as const,
  plansByStatus: (status: CropPlanStatus) => ['crop-plans', 'status', status] as const,
  plan: (planId: string) => ['crop-plans', planId] as const,
  planDetails: (planId: string) => ['crop-plans', planId, 'details'] as const,
  progress: (planId: string) => ['crop-plans', planId, 'progress'] as const,
  items: (planId: string) => ['crop-plans', planId, 'items'] as const,
  placements: (planId: string) => ['crop-plans', planId, 'placements'] as const,
};

/**
 * Hook pro ziskani vsech planu
 */
export const useCropPlans = (status?: CropPlanStatus) => {
  return useQuery({
    queryKey: status ? QUERY_KEYS.plansByStatus(status) : QUERY_KEYS.plans,
    queryFn: () => cropPlanService.getAllPlans(status),
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani aktivnich planu
 */
export const useActiveCropPlans = () => {
  return useCropPlans('active');
};

/**
 * Hook pro ziskani jednoho planu
 */
export const useCropPlan = (planId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.plan(planId || ''),
    queryFn: () => cropPlanService.getPlan(planId!),
    enabled: !!planId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani planu s detaily (items + placements)
 */
export const useCropPlanDetails = (planId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.planDetails(planId || ''),
    queryFn: () => cropPlanService.getPlanWithDetails(planId!),
    enabled: !!planId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani progresu planu
 */
export const useCropPlanProgress = (planId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.progress(planId || ''),
    queryFn: () => cropPlanService.getPlanProgress(planId!),
    enabled: !!planId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani polozek planu
 */
export const useCropPlanItems = (planId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.items(planId || ''),
    queryFn: () => cropPlanService.getItems(planId!),
    enabled: !!planId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani umisteni planu
 */
export const useCropPlanPlacements = (planId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.placements(planId || ''),
    queryFn: () => cropPlanService.getPlacements(planId!),
    enabled: !!planId,
    staleTime: 30000,
  });
};

// ===== MUTATIONS =====

/**
 * Hook pro vytvoreni noveho planu
 */
export const useCreateCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCropPlanDTO) => cropPlanService.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro aktualizaci planu
 */
export const useUpdateCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: UpdateCropPlanDTO }) =>
      cropPlanService.updatePlan(planId, data),
    onSuccess: (updatedPlan) => {
      queryClient.setQueryData(QUERY_KEYS.plan(updatedPlan.id), updatedPlan);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro smazani planu
 */
export const useDeleteCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => cropPlanService.deletePlan(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};



// ===== WORKFLOW MUTATIONS =====

/**
 * Hook pro aktivaci planu
 */
export const useActivateCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => cropPlanService.activatePlan(planId),
    onSuccess: (updatedPlan) => {
      queryClient.setQueryData(QUERY_KEYS.plan(updatedPlan.id), updatedPlan);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro dokonceni planu
 */
export const useCompleteCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => cropPlanService.completePlan(planId),
    onSuccess: (updatedPlan) => {
      queryClient.setQueryData(QUERY_KEYS.plan(updatedPlan.id), updatedPlan);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro archivaci planu
 */
export const useArchiveCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => cropPlanService.archivePlan(planId),
    onSuccess: (updatedPlan) => {
      queryClient.setQueryData(QUERY_KEYS.plan(updatedPlan.id), updatedPlan);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro exekuci planu
 */
export const useExecuteCropPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, force }: { planId: string; force?: boolean }) =>
      cropPlanService.executePlan(planId, force),
    onSuccess: () => {
      // Invalidate batches and plans
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
      queryClient.invalidateQueries({ queryKey: ['batches'] }); // Assuming batches key
    },
  });
};

// ===== ITEM MUTATIONS =====

/**
 * Hook pro pridani polozky do planu
 */
export const useAddCropPlanItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: CreateCropPlanItemDTO }) =>
      cropPlanService.addItem(planId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(variables.planId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.planDetails(variables.planId) });
    },
  });
};

/**
 * Hook pro aktualizaci polozky v planu
 */
export const useUpdateCropPlanItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: { quantity: number; notes?: string; planned_sow_date?: string; planned_transplant_date?: string; planned_harvest_date?: string } }) =>
      cropPlanService.updateItem(itemId, data),
    onSuccess: () => {
      // Ponevadz nemame planId v response nutne k invalidaci predtim nacachovanych detailu, invalidujeme globalne plants
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro odstraneni polozky z planu
 */
export const useRemoveCropPlanItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, planId: _planId }: { itemId: string; planId: string }) =>
      cropPlanService.removeItem(itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(variables.planId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.planDetails(variables.planId) });
    },
  });
};

// ===== PLACEMENT MUTATIONS =====

/**
 * Hook pro pridani umisteni
 */
export const useAddPlacement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: CreatePlacementDTO }) =>
      cropPlanService.addPlacement(itemId, data),
    onSuccess: () => {
      // Invaliduj vsechny placements - nemame planId primo
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Hook pro aktualizaci stavu umisteni
 */
export const useUpdatePlacementStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      placementId,
      status,
    }: {
      placementId: string;
      status: 'planned' | 'executed' | 'cancelled';
    }) => cropPlanService.updatePlacementStatus(placementId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

/**
 * Kombinovany hook pro kompletni spravu planu pestovani
 */
export const useCropPlanManager = () => {
  const plansQuery = useCropPlans();
  const activePlansQuery = useActiveCropPlans();
  const createMutation = useCreateCropPlan();
  const updateMutation = useUpdateCropPlan();
  const deleteMutation = useDeleteCropPlan();
  const activateMutation = useActivateCropPlan();
  const completeMutation = useCompleteCropPlan();
  const archiveMutation = useArchiveCropPlan();
  const executeMutation = useExecuteCropPlan();

  return {
    // Data
    plans: plansQuery.data || [],
    activePlans: activePlansQuery.data || [],
    isLoading: plansQuery.isLoading,
    error: plansQuery.error,

    // CRUD Actions
    refetch: plansQuery.refetch,
    createPlan: createMutation.mutateAsync,
    updatePlan: updateMutation.mutateAsync,
    deletePlan: deleteMutation.mutateAsync,

    // Workflow Actions
    activatePlan: activateMutation.mutateAsync,
    completePlan: completeMutation.mutateAsync,
    archivePlan: archiveMutation.mutateAsync,
    executePlan: executeMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isActivating: activateMutation.isPending,
    isCompleting: completeMutation.isPending,
    isArchiving: archiveMutation.isPending,
    isExecuting: executeMutation.isPending,
  };
};
